import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { planRate } from '../campaigns/planRates';
import { homeMessages as messages } from './homeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Home.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HomeRateCardProps {
  rate: number;
  artist: string;
}

/**
 * The rate card of the Pitch home (design outreach-v2 i1): the pitches a day of the artist. On
 * Label it says each artist has its own rate; below Label it is the Label offer of frame c2
 * (`outreachRate`, Label pre-selected). A member asks the workspace owner.
 */
const HomeRateCardComponent: React.FC<HomeRateCardProps> = ({ rate, artist }) => {
  const intl = useIntl();
  const { plan } = useMusic24Capabilities();
  const { isDemo } = useOutreachPlan();
  const isOwner = useMyTeamRole() === 'OWNER';
  const openUpgradePrompt = useUpgradePrompt();
  const label = planRate('label');

  const body = () => {
    if (plan === 'label') {
      return intl.formatMessage(messages.rateLabel, { rate });
    }
    if (plan === 'manager') {
      return intl.formatMessage(messages.rateManager, { rate, label });
    }
    return intl.formatMessage(messages.rateSolo, { label });
  };

  return (
    <div className={styles.rate} data-testid="home-rate-card">
      <div className={styles.rateNumber}>
        {rate}
        <small>{intl.formatMessage(messages.rateTitle, { artist })}</small>
      </div>
      <span className={shared.small}>{body()}</span>
      {plan !== 'label' &&
        (isOwner ? (
          <SMLButton
            size="sm"
            color="primary"
            omitCap
            disabled={isDemo}
            onClick={() => openUpgradePrompt(SMLUpgradeFeature.outreachRate)}
            testID="home-rate-offer"
          >
            {intl.formatMessage(messages.rateOffer, { label })}
          </SMLButton>
        ) : (
          <SMLButton size="sm" outline omitCap disabled testID="home-rate-ask-owner">
            {intl.formatMessage(messages.rateAskOwner)}
          </SMLButton>
        ))}
    </div>
  );
};

export const HomeRateCard = TypedMemo(HomeRateCardComponent);
